import React, { useContext, useState } from 'react';
import { HiOutlineCog6Tooth, HiOutlinePencilSquare, HiOutlineXMark } from 'react-icons/hi2';
import { Dialog } from '@headlessui/react';
import Button from '../../../components/button/button';
import { PlaybookContext } from '../context/PlaybookContext';

export default function PlaybookSettings({ onClose }) {
    const { action, saveSettings, settingsSaving } = useContext(PlaybookContext);
    const [editing, setEditing] = useState(false);
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const changed = description.trim() !== (action?.description || '').trim();
    const close = () => { if (!settingsSaving) onClose(); };
    const submit = async event => {
        event.preventDefault();
        if (settingsSaving || !changed) return;
        setError('');
        try {
            await saveSettings({ description: description.trim() });
            setEditing(false);
            setSaved(true);
        } catch (e) { setError(e.message); }
    };
    return (
        <Dialog open onClose={close} className="relative z-50">
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
            <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
                <div className="flex min-h-full items-center justify-center">
                    <Dialog.Panel className="flex max-h-[calc(100dvh-3rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-black/5">
                        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-6 py-5">
                            <div className="flex items-start gap-3">
                                <span className="rounded-xl bg-gray-100 p-2.5 text-gray-700"><HiOutlineCog6Tooth size={22} /></span>
                                <div>
                                    <Dialog.Title className="text-lg font-semibold tracking-tight text-gray-900">Playbook settings</Dialog.Title>
                                    <Dialog.Description className="mt-1 text-sm text-gray-500">Manage how your agent uses this playbook.</Dialog.Description>
                                </div>
                            </div>
                            <button type="button" aria-label="Close playbook settings" disabled={settingsSaving} onClick={close} className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50">
                                <HiOutlineXMark size={20} />
                            </button>
                        </header>

                        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
                            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-8">
                                <section aria-labelledby="playbook-general-heading">
                                    <div className="mb-6">
                                        <h3 id="playbook-general-heading" className="text-sm font-semibold text-gray-900">General</h3>
                                        <p className="mt-1 text-sm text-gray-500">Choose when this playbook is relevant to a conversation.</p>
                                    </div>
                                    <div className="border-t border-gray-100 pt-5">
                                        <div className="mb-3 flex items-center justify-between gap-4">
                                            {editing ? (
                                                <label htmlFor="playbook-call-description" className="text-sm font-medium text-gray-900">When should the agent use this?</label>
                                            ) : (
                                                <h4 className="text-sm font-medium text-gray-900">When should the agent use this?</h4>
                                            )}
                                            {!editing && (
                                                <button type="button" disabled={settingsSaving} onClick={() => { setDescription(action?.description || ''); setError(''); setSaved(false); setEditing(true); }} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
                                                    <HiOutlinePencilSquare size={16} /> Edit
                                                </button>
                                            )}
                                        </div>
                                        {editing ? (
                                            <textarea autoFocus id="playbook-call-description" aria-describedby="playbook-call-help" rows={5} value={description} disabled={settingsSaving} onChange={e => setDescription(e.target.value)} placeholder="For example, the customer wants to check the status of an order." className="block w-full resize-y rounded-xl border border-gray-300 bg-white p-3 text-sm leading-relaxed text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary disabled:bg-gray-50" />
                                        ) : (
                                            <p className="whitespace-pre-wrap break-words rounded-xl bg-gray-50 px-4 py-3 text-sm leading-relaxed text-gray-700">{action?.description || 'No description added yet.'}</p>
                                        )}
                                        <p id="playbook-call-help" className="mt-3 text-xs leading-relaxed text-gray-500">Describe the customer requests or situations that should trigger this playbook. Be specific about the intent your agent should recognize.</p>
                                        {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
                                    </div>
                                </section>
                            </div>

                            <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50/80 px-6 py-4">
                                <span role="status" className="text-xs text-gray-500">{settingsSaving ? 'Saving changes…' : editing && changed ? 'Unsaved changes' : saved ? 'Changes saved' : ''}</span>
                                <div className="ml-auto flex items-center gap-2">
                                    {editing ? (
                                        <>
                                            <Button type="button" text="Cancel" color="default" disabled={settingsSaving} onClick={() => { setEditing(false); setError(''); }} />
                                            <Button type="submit" text="Save changes" loading={settingsSaving} disabled={settingsSaving || !changed} />
                                        </>
                                    ) : <Button type="button" text="Done" color="default" onClick={close} />}
                                </div>
                            </footer>
                        </form>
                    </Dialog.Panel>
                </div>
            </div>
        </Dialog>
    );
}
